import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { updatePartner } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormLegal } from 'clickNgo/entities/partner/models/form/legal'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import {
  resetModifiedDataByField,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'
import Logger from 'core/utils/Logger'
import { showError } from 'onboarding/classes/services/notification'
import { cleanPropIfString } from 'onboarding/utils/object'

import { PartnerDataFormKeys, PartnerDataFormSettings } from '../../../configs'
import {
  PORTAL_PARTNER_STALE_DATA_MESSAGE,
  PORTAL_PARTNER_STATUS_CHECK_FAILED_MESSAGE,
  preparePartnerForSave,
} from '../../../helpers/preparePartnerForSave'
import { IPartnerDataFormData } from '../../../models'
import { PARTNER_LEGAL_INFO_EDITABLE_FIELDS } from '../configs'

interface IUseEditPartnerLegalInfoHandlerParams {
  partner: IPartner
}

export const useEditPartnerLegalInfoHandler = ({
  partner,
}: IUseEditPartnerLegalInfoHandlerParams) => {
  const dispatch = useAppDispatch()
  const [actualData, updateActualData] = useGetFormDataField<IPartnerDataFormData>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.ActualData
  )
  const [_, handleLegalChange] = useGetFormData<IPartner>(PartnerDataFormKeys.LegalInfo)

  const handleOnPartnerLegalChangesApply = useCallback(
    async ({ modifiedFields, formData, invalidFields }: TForm<IPartnerFormLegal>) => {
      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        Logger.error('HAS INVALID FIELDS')
        return false
      }

      const updatedPartnerData: IPartner = {
        ...partner,
        ...formData,
        legalModifiedValues: modifiedFields,
      }
      cleanPropIfString(updatedPartnerData)

      let partnerForSave: IPartner

      try {
        const preparationResult = await preparePartnerForSave(updatedPartnerData)

        if (preparationResult.status === 'stale') {
          showError(PORTAL_PARTNER_STALE_DATA_MESSAGE, { autoHide: false })
          return false
        }

        partnerForSave = preparationResult.partner
      } catch (error) {
        Logger.error(error)
        showError(PORTAL_PARTNER_STATUS_CHECK_FAILED_MESSAGE, { autoHide: false })
        return false
      }

      const result = await updatePartner(partnerForSave)

      if (result.status === OnboardingResponseStatus.Success) {
        updateActualData({
          ...actualData,
          partner: {
            ...partnerForSave,
            ...result.data,
          },
        })
        PARTNER_LEGAL_INFO_EDITABLE_FIELDS.forEach((field) => {
          const value = partnerForSave[field]
          handleLegalChange({ field: field as keyof IPartner, value })
        })

        return true
      }

      if (result.status === OnboardingResponseStatus.Fail) {
        const messages = getJSendErrorMessages(result).join('. ')
        Logger.error(messages)
      } else {
        Logger.error(result.message)
        return false
      }

      Logger.error('Unknown error')
      return false
    },
    [actualData, handleLegalChange, partner, updateActualData]
  )

  const handleOnPartnerLegalChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormLegal>) => {
      modifiedFields.forEach((field) => {
        dispatch(resetModifiedDataByField({ formKey: PartnerDataFormKeys.LegalInfo, field }))
      })
    },
    [dispatch]
  )

  return { handleOnPartnerLegalChangesApply, handleOnPartnerLegalChangesCancel }
}
